import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ITEM_STATUS_LABELS_RU } from '@crm/shared'
import type { RequestItemStatus } from '@crm/shared'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { getPartCard, type PartCard } from '../../shared/api/catalog.api'
import { SOURCE_LABELS } from '../request-items/components/AddResultForm'
import { useAuthStore } from '../auth/authStore'
import { PartIdentityEditForm } from './PartIdentityEditForm'

const fmtInt = (n: number) => n.toLocaleString('ru-RU')
const fmtMoney = (n: number) =>
  n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—')
const statusRu = (s: string) => ITEM_STATUS_LABELS_RU[s as RequestItemStatus] ?? s
const sourceRu = (s: string) => SOURCE_LABELS[s as keyof typeof SOURCE_LABELS] ?? s

const SOURCE_BADGE: Record<PartCard['identity']['source'], { label: string; cls: string }> = {
  both: { label: 'УНФ + правка', cls: 'bg-violet-100 text-violet-700' },
  learned: { label: 'Ручная категория', cls: 'bg-amber-100 text-amber-700' },
  ref: { label: 'Справочник УНФ', cls: 'bg-sky-100 text-sky-700' },
  // Парт заведён автоматически из позиции заявки, наименование ещё не задано.
  item: { label: 'Из заявки', cls: 'bg-gray-100 text-gray-600' },
  none: { label: 'Нет в каталоге', cls: 'bg-gray-100 text-gray-600' },
}

// Карточка партномера: вся информация по парту (партномер = артикул). A —
// идентификация, B — спрос (заявки), C — закупка (квоты), D — продажи (заказы).
export default function PartCardPage() {
  const { artKey = '' } = useParams()
  const decoded = decodeURIComponent(artKey)
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'ADMIN' || role === 'ROP'
  const [editOpen, setEditOpen] = useState(false)

  const query = useQuery({
    queryKey: ['catalog', 'part', decoded],
    queryFn: () => getPartCard(decoded),
  })

  if (query.isLoading) return <PageLoader />
  if (query.isError) return <ErrorMessage error={query.error} />
  const card = query.data!
  const { identity: id, demand, procurement, sales } = card
  const badge = SOURCE_BADGE[id.source]

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div>
        <Link to="/catalog" className="text-sm text-blue-600 hover:underline">
          ← К каталогу
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{id.article}</h1>
          {id.category && (
            <span className="text-sm px-2 py-0.5 rounded-full bg-brand-100 text-brand-700">
              {id.category}
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
          {canEdit && !editOpen && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              title="Изменить бренд / наименование / сегмент — только каталог и новые заявки, существующие заявки не меняются"
              className="text-xs px-2 py-1 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Редактировать
            </button>
          )}
        </div>
        {id.name && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{id.name}</p>}
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          {id.catalogBrand && <span>Бренд (каталог): {id.catalogBrand}</span>}
          {id.brands.length > 0 && <span>Бренды: {id.brands.join(', ')}</span>}
          {id.unfCode && <span>Код УНФ: {id.unfCode}</span>}
          {id.unit && <span>Ед.: {id.unit}</span>}
          <span>Ключ: {id.artKey}</span>
        </div>
        {editOpen && <PartIdentityEditForm identity={id} onClose={() => setEditOpen(false)} />}
      </div>

      {/* Воронка */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Запрашивали (позиций)" value={fmtInt(sales.funnel.requested)} />
        <Kpi label="Квотировали" value={fmtInt(sales.funnel.quoted)} accent="text-blue-700" />
        <Kpi label="Заказали" value={fmtInt(sales.funnel.ordered)} accent="text-green-700" />
      </div>

      {/* B. Спрос */}
      <Section title="Спрос — где встречался">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Kpi label="Позиций в заявках" value={fmtInt(demand.itemsCount)} small />
          <Kpi label="Заявок" value={fmtInt(demand.requestsCount)} small />
          <Kpi label="Суммарно, шт" value={fmtInt(demand.totalQuantity)} small />
          <Kpi
            label="Период"
            value={`${fmtDate(demand.firstSeen)} — ${fmtDate(demand.lastSeen)}`}
            small
          />
        </div>
        {demand.topClients.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {demand.topClients.map((c) => (
              <span
                key={c.name}
                className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              >
                {c.name} · {c.count}
              </span>
            ))}
          </div>
        )}
        {demand.requests.length === 0 ? (
          <Empty text="Партномер ещё не встречался в заявках." />
        ) : (
          <Table head={['Заявка', 'Клиент', 'Менеджер', 'Бренд', 'Кол-во', 'Статус', 'Дата']}>
            {demand.requests.map((r) => (
              <tr key={r.itemId} className={ROW}>
                <Td>
                  <Link to={`/requests/${r.requestId}`} className="text-blue-600 hover:underline">
                    {r.requestNumber ?? r.requestId.slice(0, 8)}
                  </Link>
                </Td>
                <Td>{r.clientName ?? '—'}</Td>
                <Td className="text-gray-500">{r.managerName ?? '—'}</Td>
                <Td>{r.brand ?? '—'}</Td>
                <Td className="text-right">{fmtInt(r.quantity)}</Td>
                <Td>{statusRu(r.status)}</Td>
                <Td className="text-gray-500 whitespace-nowrap">{fmtDate(r.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
        {demand.itemsCount > demand.requests.length && (
          <p className="mt-2 text-xs text-gray-400">
            Показаны первые {demand.requests.length} из {fmtInt(demand.itemsCount)}.
          </p>
        )}
      </Section>

      {/* C. Закупка */}
      <Section title="Закупка — цены поставщиков">
        {procurement.byCurrency.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {procurement.byCurrency.map((c) => (
              <div
                key={c.currency}
                className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/40"
              >
                <div className="text-xs text-gray-500">{c.currency}: мин / последняя</div>
                <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {fmtMoney(c.minPrice)} / {fmtMoney(c.lastPrice)}
                </div>
              </div>
            ))}
          </div>
        )}
        {procurement.quotes.length === 0 ? (
          <Empty text="По партномеру ещё нет квот поставщиков." />
        ) : (
          <Table head={['Поставщик', 'Матрица', 'Цена', 'С коэф.', 'Автор', 'Дата']}>
            {procurement.quotes.map((q) => (
              <tr key={q.id} className={ROW}>
                <Td>{q.supplierName}</Td>
                <Td>{sourceRu(q.sourceType)}</Td>
                <Td className="text-right whitespace-nowrap">
                  {q.price !== null ? `${fmtMoney(q.price)} ${q.currency}` : '—'}
                </Td>
                <Td className="text-right whitespace-nowrap text-gray-500">
                  {q.priceWithCoeff !== null ? `${fmtMoney(q.priceWithCoeff)} ${q.currency}` : '—'}
                </Td>
                <Td className="text-gray-500">{q.creatorName ?? '—'}</Td>
                <Td className="text-gray-500 whitespace-nowrap">{fmtDate(q.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
        {procurement.quotesCount > procurement.quotes.length && (
          <p className="mt-2 text-xs text-gray-400">
            Показаны первые {procurement.quotes.length} из {fmtInt(procurement.quotesCount)}.
          </p>
        )}
      </Section>

      {/* D. Продажи */}
      <Section title="Продажи — заказы">
        {sales.orders.length === 0 ? (
          <Empty text="Партномер ещё не уходил в заказы." />
        ) : (
          <>
            <p className="mb-3 text-sm text-gray-600">
              В заказах суммарно: <b>{fmtInt(sales.orderedQuantity)}</b> шт
            </p>
            <Table head={['Заказ (заявка)', 'Кол-во', 'Цена', 'Сумма', 'Дата']}>
              {sales.orders.map((o, idx) => (
                <tr key={`${o.orderId}:${idx}`} className={ROW}>
                  <Td>
                    <Link to={`/orders/${o.orderId}`} className="text-blue-600 hover:underline">
                      {o.requestNumber ?? o.orderId.slice(0, 8)}
                    </Link>
                  </Td>
                  <Td className="text-right">{fmtInt(o.quantity)}</Td>
                  <Td className="text-right whitespace-nowrap">{fmtMoney(o.price)} ₽</Td>
                  <Td className="text-right whitespace-nowrap">{fmtMoney(o.sum)} ₽</Td>
                  <Td className="text-gray-500 whitespace-nowrap">{fmtDate(o.createdAt)}</Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Section>
    </div>
  )
}

const ROW = 'border-b border-gray-100 dark:border-gray-800 last:border-0'

function Kpi({
  label,
  value,
  accent,
  small,
}: {
  label: string
  value: string
  accent?: string
  small?: boolean
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={`mt-1 font-semibold ${small ? 'text-base' : 'text-xl'} ${accent ?? 'text-gray-900 dark:text-gray-100'}`}
      >
        {value}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
      <h2 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100">{title}</h2>
      {children}
    </div>
  )
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
            {head.map((h) => (
              <th key={h} className="py-2 pr-4 font-medium whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`py-2 pr-4 ${className}`}>{children}</td>
}

function Empty({ text }: { text: string }) {
  return <p className="py-4 text-sm text-gray-400">{text}</p>
}
